import { FC, useContext } from 'react';

import {
  FixIcon,
  MonitorArrowInIcon,
  ShellIcon,
  ShutdownIcon,
  WalletArrowLeftIcon,
} from '@smartshell/ui-kit';

import { ReportSummaryBlock, ReportSummaryTooltip } from 'components';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { formatMoney, useSummaryValues } from '../utils';
import { IOperatingSummaryProps } from './interfaces';
import s from './OperatingSummary.module.scss';

export const OperatingSummary: FC<IOperatingSummaryProps> = ({
  data,
  loading,
}) => {
  const { t } = useContext(LocalizationContext);
  const { currency } = useContext(SettingsContext).state.settings;

  const { getValue, getExtraValue } = useSummaryValues(data, loading);

  const normalizedData = [
    {
      text: t('visitors.club.load'),
      value: `${getValue('total_load_percent')}%`,
      icon: <ShutdownIcon color="secondary" />,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.club.load')}
          footer={t('visitors.operation.club.text')}
          variant="secondary"
        />
      ),
    },
    {
      text: t('session'),
      value: `${getValue('total_sessions')} ${t('pcs')}`,
      icon: <ShellIcon color="secondary" />,
      percent: `${getExtraValue('total_sessions')} ${t('hour')}`,
      tooltip: (
        <ReportSummaryTooltip
          title={t('session')}
          text_1={t('report.hosts.total.sessions')}
          value_1={`${getValue('total_sessions')} ${t('pcs')}`}
          text_2={t('session.duration')}
          value_2={`${getExtraValue('total_sessions')} ${t('hour')}`}
          footer={t('session.tooltip.footer')}
          variant="primary"
        />
      ),
    },
    {
      text: t('visitors.rental.cost'),
      value: `${formatMoney(getValue('avg_hour_cost'), currency.sign)}`,
      icon: <FixIcon color="secondary" />,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.rental.cost')}
          footer={t('visitors.rental.cost.text')}
          variant="secondary"
        />
      ),
    },
    {
      text: t('visitors.online.booking'),
      value: `${getValue('total_booked_sessions')} ${t('pcs')}`,

      icon: <MonitorArrowInIcon color="secondary" />,
      percent: `${getExtraValue('total_booked_sessions')} ${t('hour')}`,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.online.booking')}
          text_1={t('count.online.booking')}
          value_1={`${getValue('total_booked_sessions')} ${t('pcs')}`}
          text_2={t('middle.session.duration')}
          value_2={`${getExtraValue('total_booked_sessions')} ${t('hour')}`}
          footer={t('session.tooltip.footer_2')}
          variant="primary"
        />
      ),
    },
    {
      text: t('visitors.online.booking.revenue'),
      value: `${formatMoney(getValue('total_booked_revenue'), currency.sign)}`,
      icon: <WalletArrowLeftIcon color="secondary" />,
      percent: `${formatMoney(
        getExtraValue('total_booked_revenue'),
        currency.sign
      )}`,
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.online.booking.revenue')}
          text_1={t('revenue.sum.booking')}
          value_1={`${formatMoney(
            getValue('total_booked_revenue'),
            currency.sign
          )}`}
          text_2={t('middle.check.booking')}
          value_2={`${formatMoney(
            getExtraValue('total_booked_revenue'),
            currency.sign
          )}`}
          footer={t('middle.check.booking.footer')}
          variant="primary"
        />
      ),
    },
  ];

  return (
    <div className={s.container}>
      {normalizedData.map((item) => (
        <ReportSummaryBlock key={item.text} item={item} />
      ))}
    </div>
  );
};
